import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { ErrorBoundary } from './components/ErrorBoundary';
import { GlobalTranslationProvider } from './components/GlobalTranslationProvider';
import { Layout } from './components/Layout';
import { AuthProvider, useAuthContext } from './contexts/BetterAuthContext';
import { PrioritySettingsProvider } from './contexts/PrioritySettingsContext';
import { SettingsProvider } from './contexts/SettingsContext';
// FR-92: lifts the selected What-If scenario to app scope so surfaces other than
// the dashboard widget can observe it. Previously it lived in component-local
// useState, which is why nothing could react to the simulation.
import { SimulationStateProvider } from './contexts/SimulationStateContext';

// Lazy-loaded page components
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Activities = lazy(() => import('./pages/Activities'));
const Priorities = lazy(() => import('./pages/Priorities'));
const Messages = lazy(() => import('./pages/Messages'));
const MessagesCategories = lazy(() => import('./pages/MessagesCategories'));
const Documents = lazy(() => import('./pages/Documents'));
const Contracts = lazy(() => import('./pages/Contracts'));
const Buyers = lazy(() => import('./pages/Buyers'));
const Suppliers = lazy(() => import('./pages/Suppliers'));
const Receivables = lazy(() => import('./pages/Receivables'));
const Payables = lazy(() => import('./pages/Payables'));
const Approvals = lazy(() => import('./pages/Approvals'));
const NewApproval = lazy(() => import('./pages/NewApproval'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Settings = lazy(() => import('./pages/Settings'));
const AIDebug = lazy(() => import('./pages/AIDebug'));
// PaymentsOut removed — Payables covers outgoing payment tracking
const NotFound = lazy(() => import('./pages/NotFound'));
const TestAutomation = lazy(() => import('./pages/TestAutomation'));
const TradeAssuranceDemo = lazy(() => import('./pages/TradeAssuranceDemo'));
const SystemsSetup = lazy(() => import('./pages/SystemsSetup'));
const InvoiceCreation = lazy(() => import('./pages/InvoiceCreation'));
const Forex = lazy(() => import('./pages/Forex'));
const Support = lazy(() => import('./pages/Support'));
// PaymentsIn removed — Receivables covers incoming payment tracking
const CreatePayment = lazy(() => import('./pages/CreatePayment'));
const ExcelLikeDemo = lazy(() => import('./pages/ExcelLikeDemo'));
const USMCACompliancePage = lazy(() => import('./pages/USMCACompliance'));
const Account = lazy(() => import('./pages/Account'));
const TellTaleTest = lazy(() => import('./pages/TellTaleTest'));
const TellTaleTestRunner = lazy(() => import('./pages/TellTaleTestRunner'));
const TestHub = lazy(() => import('./pages/tests/TestHub'));
const TellTaleTests = lazy(() => import('./pages/tests/TellTaleTests'));
const DocumentTests = lazy(() => import('./pages/tests/DocumentTests'));
const AccountingIntegrations = lazy(() => import('./pages/AccountingIntegrations'));
const Quotes = lazy(() => import('./pages/Quotes'));
const QuoteDetail = lazy(() => import('./pages/QuoteDetail'));
const QuoteEdit = lazy(() => import('./pages/QuoteEdit'));
const QuoteNew = lazy(() => import('./pages/QuoteNew'));
const Leads = lazy(() => import('./pages/Leads'));
const RFQDashboard = lazy(() => import('./pages/RFQDashboard'));
const NotificationTest = lazy(() => import('./pages/NotificationTest'));
const SecretaryEconomyPage = lazy(() => import('./pages/SecretaryEconomy'));
const TremecCompliancePage = lazy(() => import('./pages/TremecCompliance'));
const TremecDemoPage = lazy(() => import('./pages/TremecDemo'));
const FactoringPage = lazy(() => import('./pages/Factoring'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const SupplierResponse = lazy(() => import('./pages/SupplierResponse'));
const NexteerPortal = lazy(() => import('./pages/NexteerPortal'));
const KatconPortalPage = lazy(() => import('./pages/KatconPortal'));
const NexteerPortalPage = lazy(() => import('./demo/nexteer/NexteerCompliancePortal'));
const ToyotaCompliancePortal = lazy(() => import('./pages/ToyotaCompliancePortal'));
const BMWCompliancePortal = lazy(() => import('./pages/BMWCompliancePortal'));
const DensoCompliancePortal = lazy(() => import('./pages/DensoCompliancePortal'));
const BroseCompliancePortal = lazy(() => import('./pages/BroseCompliancePortal'));
const GDLSCompliancePortal = lazy(() => import('./pages/GDLSCompliancePortal'));
const MetalsaCompliancePortal = lazy(() => import('./pages/MetalsaCompliancePortal'));
const FordCompliancePortal = lazy(() => import('./pages/FordCompliancePortal'));
const MercedesCompliancePortal = lazy(() => import('./pages/MercedesCompliancePortal'));
const CustomsComplianceDashboard = lazy(() => import('./pages/customs/compliance-dashboard'));
const CustomsShipmentDetailPage = lazy(() => import('./pages/customs/shipment-detail'));
const CustomsCdrViewPage = lazy(() => import('./pages/customs/cdr-view'));
const CustomsClarificationsPage = lazy(() => import('./pages/customs/clarifications'));
const CustomsClarificationDetailPage = lazy(() => import('./pages/customs/clarification-detail'));
const CustomsSupplierScorecardsPage = lazy(() => import('./pages/customs/supplier-scorecard'));
const CustomsSupplierDetailPage = lazy(() => import('./pages/customs/supplier-detail'));
const CustomsSupplierHealthPage = lazy(() => import('./pages/customs/supplier-health'));
const CustomsRegulatoryCalendarPage = lazy(() => import('./pages/customs/regulatory-calendar'));
const CustomsOnboardingWizardPage = lazy(() => import('./pages/customs/onboarding-wizard'));
const CustomsAnalyticsPage = lazy(() => import('./pages/customs/analytics'));
const CustomsAnalyticsSavingsPage = lazy(() => import('./pages/customs/analytics-savings'));
const CustomsErpPage = lazy(() => import('./pages/customs/erp'));
const BrokerPortalPage = lazy(() => import('./pages/broker/BrokerPortalPage'));
const BrokerItemPage = lazy(() => import('./pages/broker/BrokerItemPage'));
const BrokerPreferencesPage = lazy(() => import('./pages/broker/BrokerPreferencesPage'));

// Bank Portal Pages
const BankDashboard = lazy(() => import('./pages/bank/BankDashboard'));
const BankCompanyDetail = lazy(() => import('./pages/bank/BankCompanyDetail'));
const BankInvoiceDetail = lazy(() => import('./pages/bank/BankInvoiceDetail'));
const MexicanCapitalPartner = lazy(() => import('./pages/bank/MexicanCapitalPartner'));
const MexicanJacketDetail = lazy(() => import('./pages/bank/MexicanJacketDetail'));
const DataGovernance = lazy(() => import('./pages/bank/DataGovernance'));

const queryClient = new QueryClient();

function App() {
  // Set the document title
  useEffect(() => {
    document.title = 'Alisio - AI-Powered Financial Operations';
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AuthProvider>
          <SettingsProvider>
            <PrioritySettingsProvider>
              <GlobalTranslationProvider>
                <SimulationStateProvider>
                  <QueryClientProvider client={queryClient}>
                    <Suspense
                      fallback={
                        <div className="flex items-center justify-center h-screen">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                      }
                    >
                      <Routes>
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/accept-invite" element={<AcceptInvite />} />
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        <Route path="/respond/:token" element={<SupplierResponse />} />
                        <Route path="/broker/:token" element={<BrokerPortalPage />} />
                        <Route path="/broker/:token/items/:itemId" element={<BrokerItemPage />} />
                        <Route
                          path="/broker/:token/preferences"
                          element={<BrokerPreferencesPage />}
                        />
                        <Route
                          path="/onboarding"
                          element={
                            <Layout>
                              <Onboarding />
                            </Layout>
                          }
                        />
                        <Route
                          path="/dashboard"
                          element={
                            <Layout>
                              <Dashboard />
                            </Layout>
                          }
                        />
                        <Route
                          path="/activities"
                          element={
                            <Layout>
                              <Activities />
                            </Layout>
                          }
                        />
                        <Route
                          path="/priorities"
                          element={
                            <Layout>
                              <Priorities />
                            </Layout>
                          }
                        />

                        {/* Add missing insights routes */}
                        <Route
                          path="/insights"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />
                        <Route
                          path="/insights/cash-flow"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />
                        <Route
                          path="/insights/payment-terms"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />
                        <Route
                          path="/insights/payment-patterns"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />
                        <Route
                          path="/insights/fx-hedging"
                          element={
                            <Layout>
                              <Forex />
                            </Layout>
                          }
                        />
                        <Route
                          path="/insights/supplier-optimization"
                          element={
                            <Layout>
                              <Suppliers />
                            </Layout>
                          }
                        />
                        <Route
                          path="/insights/market-expansion"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />
                        <Route
                          path="/insights/churn-prediction"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />
                        <Route
                          path="/insights/working-capital"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />

                        {/* Add missing recommendations routes */}
                        <Route
                          path="/recommendations/alisio"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />
                        <Route
                          path="/recommendations/invoice-automation"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />
                        <Route
                          path="/recommendations/payment-terms"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />
                        <Route
                          path="/recommendations/alisio/invoice-automation"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />
                        <Route
                          path="/recommendations/alisio/payment-optimization"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />
                        <Route
                          path="/recommendations/alisio/currency-hedging"
                          element={
                            <Layout>
                              <Forex />
                            </Layout>
                          }
                        />
                        <Route
                          path="/recommendations/alisio/compliance-automation"
                          element={
                            <Layout>
                              <USMCACompliancePage />
                            </Layout>
                          }
                        />
                        <Route
                          path="/recommendations/alisio/customer-expansion"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />
                        <Route
                          path="/recommendations/alisio/reporting-enhancement"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />

                        {/* Add missing dashboard sub-routes */}
                        <Route
                          path="/dashboard/cash-flow"
                          element={
                            <Layout>
                              <Dashboard />
                            </Layout>
                          }
                        />
                        <Route
                          path="/dashboard/payment-acceleration"
                          element={
                            <Layout>
                              <Dashboard />
                            </Layout>
                          }
                        />
                        <Route
                          path="/messages"
                          element={
                            <Layout>
                              <Messages />
                            </Layout>
                          }
                        />

                        {/* Add missing message sub-routes */}
                        <Route
                          path="/messages/priorities"
                          element={
                            <Layout>
                              <Priorities />
                            </Layout>
                          }
                        />
                        <Route
                          path="/messages/categories"
                          element={
                            <Layout>
                              <MessagesCategories />
                            </Layout>
                          }
                        />

                        <Route
                          path="/documents"
                          element={
                            <Layout>
                              <Documents />
                            </Layout>
                          }
                        />
                        <Route
                          path="/contracts"
                          element={
                            <Layout>
                              <Contracts />
                            </Layout>
                          }
                        />
                        <Route
                          path="/buyers"
                          element={
                            <Layout>
                              <Buyers />
                            </Layout>
                          }
                        />
                        <Route
                          path="/suppliers"
                          element={
                            <Layout>
                              <Suppliers />
                            </Layout>
                          }
                        />

                        {/* Add missing supplier sub-routes */}
                        <Route
                          path="/suppliers/onboarding"
                          element={
                            <Layout>
                              <Suppliers />
                            </Layout>
                          }
                        />
                        <Route
                          path="/suppliers/analytics"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />
                        <Route
                          path="/suppliers/contracts"
                          element={
                            <Layout>
                              <Contracts />
                            </Layout>
                          }
                        />

                        <Route
                          path="/receivables"
                          element={
                            <Layout>
                              <Receivables />
                            </Layout>
                          }
                        />

                        {/* Add missing receivables sub-routes */}
                        <Route
                          path="/receivables/credits"
                          element={
                            <Layout>
                              <Receivables />
                            </Layout>
                          }
                        />
                        <Route
                          path="/receivables/payments"
                          element={
                            <Layout>
                              <Receivables />
                            </Layout>
                          }
                        />

                        <Route
                          path="/payables"
                          element={
                            <Layout>
                              <Payables />
                            </Layout>
                          }
                        />

                        {/* Payments In/Out removed — Receivables and Payables cover this */}
                        <Route
                          path="/payments/vendors"
                          element={
                            <Layout>
                              <Suppliers />
                            </Layout>
                          }
                        />

                        <Route
                          path="/approvals"
                          element={
                            <Layout>
                              <Approvals />
                            </Layout>
                          }
                        />
                        <Route
                          path="/new-approval"
                          element={
                            <Layout>
                              <NewApproval />
                            </Layout>
                          }
                        />
                        <Route
                          path="/analytics"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />

                        {/* Add missing analytics sub-routes */}
                        <Route
                          path="/analytics/reports"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />
                        <Route
                          path="/analytics/export"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />
                        <Route
                          path="/analytics/forecast"
                          element={
                            <Layout>
                              <Analytics />
                            </Layout>
                          }
                        />

                        <Route
                          path="/settings"
                          element={
                            <Layout>
                              <Settings />
                            </Layout>
                          }
                        />

                        {/* Add missing settings sub-routes */}
                        <Route
                          path="/settings/users"
                          element={
                            <Layout>
                              <Settings />
                            </Layout>
                          }
                        />
                        <Route
                          path="/settings/integrations"
                          element={
                            <Layout>
                              <Settings />
                            </Layout>
                          }
                        />
                        <Route
                          path="/settings/ai-tuning"
                          element={
                            <Layout>
                              <Settings />
                            </Layout>
                          }
                        />

                        <Route
                          path="/ai-debug"
                          element={
                            <Layout>
                              <AIDebug />
                            </Layout>
                          }
                        />
                        {/* payments-out routes removed — Payables covers outgoing payments */}

                        {/* Add missing invoice routes */}
                        <Route
                          path="/invoice-creation"
                          element={
                            <Layout>
                              <InvoiceCreation />
                            </Layout>
                          }
                        />

                        {/* Add Create Payment route */}
                        <Route
                          path="/create-payment"
                          element={
                            <Layout>
                              <CreatePayment />
                            </Layout>
                          }
                        />

                        {/* Add Test Automation Route */}
                        <Route
                          path="/test-automation"
                          element={
                            <Layout>
                              <TestAutomation />
                            </Layout>
                          }
                        />

                        {/* Test Hub Routes */}
                        <Route
                          path="/tests"
                          element={
                            <Layout>
                              <TestHub />
                            </Layout>
                          }
                        />

                        <Route
                          path="/tests/telltale"
                          element={
                            <Layout>
                              <TellTaleTests />
                            </Layout>
                          }
                        />

                        <Route
                          path="/tests/documents"
                          element={
                            <Layout>
                              <DocumentTests />
                            </Layout>
                          }
                        />

                        {/* Legacy TellTale Test Routes (keeping for backwards compatibility) */}
                        <Route
                          path="/telltale-test"
                          element={
                            <Layout>
                              <TellTaleTest />
                            </Layout>
                          }
                        />

                        <Route
                          path="/telltale-test-runner"
                          element={
                            <Layout>
                              <TellTaleTestRunner />
                            </Layout>
                          }
                        />

                        <Route path="/trade-assurance-demo" element={<TradeAssuranceDemo />} />

                        <Route
                          path="/systems-setup"
                          element={
                            <Layout>
                              <SystemsSetup />
                            </Layout>
                          }
                        />

                        {/* Add missing Excel demo route */}
                        <Route
                          path="/excel-demo"
                          element={
                            <Layout>
                              <ExcelLikeDemo />
                            </Layout>
                          }
                        />

                        {/* Add missing Forex route */}
                        <Route
                          path="/forex"
                          element={
                            <Layout>
                              <Forex />
                            </Layout>
                          }
                        />

                        {/* Add missing Support route */}
                        <Route
                          path="/support"
                          element={
                            <Layout>
                              <Support />
                            </Layout>
                          }
                        />

                        <Route
                          path="/usmca-compliance"
                          element={
                            <Layout>
                              <USMCACompliancePage />
                            </Layout>
                          }
                        />

                        <Route
                          path="/customs"
                          element={
                            <Layout>
                              <CustomsComplianceDashboard />
                            </Layout>
                          }
                        />
                        <Route
                          path="/customs/shipments/:id"
                          element={
                            <Layout>
                              <CustomsShipmentDetailPage />
                            </Layout>
                          }
                        />
                        <Route
                          path="/customs/cdrs/:id"
                          element={
                            <Layout>
                              <CustomsCdrViewPage />
                            </Layout>
                          }
                        />
                        <Route
                          path="/customs/clarifications"
                          element={
                            <Layout>
                              <CustomsClarificationsPage />
                            </Layout>
                          }
                        />
                        <Route
                          path="/customs/clarifications/:id"
                          element={
                            <Layout>
                              <CustomsClarificationDetailPage />
                            </Layout>
                          }
                        />
                        <Route
                          path="/customs/suppliers"
                          element={
                            <Layout>
                              <CustomsSupplierScorecardsPage />
                            </Layout>
                          }
                        />
                        <Route
                          path="/customs/suppliers/:id"
                          element={
                            <Layout>
                              <CustomsSupplierDetailPage />
                            </Layout>
                          }
                        />
                        <Route
                          path="/customs/health"
                          element={
                            <Layout>
                              <CustomsSupplierHealthPage />
                            </Layout>
                          }
                        />
                        <Route
                          path="/customs/calendar"
                          element={
                            <Layout>
                              <CustomsRegulatoryCalendarPage />
                            </Layout>
                          }
                        />
                        <Route
                          path="/onboarding/customs/resume"
                          element={
                            <Layout>
                              <CustomsOnboardingWizardPage />
                            </Layout>
                          }
                        />
                        <Route
                          path="/onboarding/customs/step/:stepId"
                          element={
                            <Layout>
                              <CustomsOnboardingWizardPage />
                            </Layout>
                          }
                        />
                        <Route
                          path="/customs/analytics"
                          element={
                            <Layout>
                              <CustomsAnalyticsPage />
                            </Layout>
                          }
                        />
                        <Route
                          path="/customs/analytics/savings/:month"
                          element={
                            <Layout>
                              <CustomsAnalyticsSavingsPage />
                            </Layout>
                          }
                        />
                        <Route
                          path="/customs/erp"
                          element={
                            <Layout>
                              <CustomsErpPage />
                            </Layout>
                          }
                        />

                        {/* Add Account/Profile route */}
                        <Route
                          path="/account"
                          element={
                            <Layout>
                              <Account />
                            </Layout>
                          }
                        />

                        {/* Add Accounting Integrations route */}
                        <Route
                          path="/accounting-integrations"
                          element={
                            <Layout>
                              <AccountingIntegrations />
                            </Layout>
                          }
                        />

                        {/* Quote Management routes */}
                        <Route
                          path="/quotes"
                          element={
                            <Layout>
                              <Quotes />
                            </Layout>
                          }
                        />
                        <Route
                          path="/quotes/new"
                          element={
                            <Layout>
                              <QuoteNew />
                            </Layout>
                          }
                        />
                        <Route
                          path="/quotes/:id/edit"
                          element={
                            <Layout>
                              <QuoteEdit />
                            </Layout>
                          }
                        />
                        <Route
                          path="/quotes/:id"
                          element={
                            <Layout>
                              <QuoteDetail />
                            </Layout>
                          }
                        />
                        <Route
                          path="/leads"
                          element={
                            <Layout>
                              <Leads />
                            </Layout>
                          }
                        />
                        <Route
                          path="/rfq-dashboard"
                          element={
                            <Layout>
                              <RFQDashboard />
                            </Layout>
                          }
                        />
                        <Route
                          path="/notification-test"
                          element={
                            <Layout>
                              <NotificationTest />
                            </Layout>
                          }
                        />

                        {/* Secretary of Economy — Capital Stack */}
                        <Route
                          path="/secretary-economy"
                          element={
                            <Layout>
                              <SecretaryEconomyPage />
                            </Layout>
                          }
                        />

                        {/* TREMEC Compliance Command Center */}
                        <Route
                          path="/tremec-compliance"
                          element={
                            <Layout>
                              <TremecCompliancePage />
                            </Layout>
                          }
                        />

                        {/* TREMEC Unified Demo — standalone, like BMW/Denso/Brose.
                      No Layout wrapper: this is a BUYER-facing portal for
                      TREMEC's own purchasing and customs people, and wrapping
                      it in the Alisio controller shell put someone else's
                      sidebar (Panel de Control, Cuentas por Cobrar, Forex)
                      around it — chrome for a persona who is not in the room,
                      and a nav that leads out of the demo mid-meeting. */}
                        <Route path="/tremec-demo" element={<TremecDemoPage />} />

                        {/* Katcon Compliance Portal */}
                        <Route
                          path="/katcon-portal"
                          element={
                            <Layout>
                              <KatconPortalPage />
                            </Layout>
                          }
                        />

                        {/* Nexteer Automotive Compliance Portal */}
                        <Route
                          path="/nexteer-portal"
                          element={
                            <Layout>
                              <NexteerPortalPage />
                            </Layout>
                          }
                        />

                        {/* Toyota Compliance Portal — Supply Chain Risk (SCR) demo tenant
                      No Layout wrapper: portal is fully branded and stands alone.
                      Tabs: overview, inbox, extraction, rvc, subtier, alerts, attestation. */}
                        <Route path="/toyota-compliance" element={<ToyotaCompliancePortal />} />

                        {/* BMW Mexico Pilot — Compliance Command Center demo tenant.
                      Gated behind VITE_ENABLE_BMW_DEMO=1 so the pilot is not
                      publicly discoverable until iVentures has seen it. Local
                      dev and Railway staging set the var to '1'; production
                      leaves it unset until BMW Group corp legal sign-off.
                      No Layout wrapper: portal is fully branded and stands
                      alone. */}
                        {import.meta.env.VITE_ENABLE_BMW_DEMO === '1' && (
                          <Route path="/bmw-compliance" element={<BMWCompliancePortal />} />
                        )}

                        {/* DENSO Mexico Compliance Portal — inbound/procurement demo
                      tenant (Monday). Structural clone of BMW. Frame: SAT/CFDI/
                      EFOS/32-D verification, Section 301 stacking on ADAS/IVI
                      Asian-origin content, USMCA RVC. EN/ES/JA. Ungated —
                      anchor demo must render for the meeting. */}
                        <Route path="/denso-compliance" element={<DensoCompliancePortal />} />

                        {/* Ford North America Compliance Portal — deck/screenshot demo
                      tenant. Frame: traceability, USMCA rules of origin,
                      government reporting (CBP/CBSA/SAT), Tier-1/sub-tier
                      evidence chasing across Ford's NA assembly footprint.
                      No i18n, no persona toggle — illustrative/fictional
                      supplier data only. Ungated. */}
                        <Route path="/ford-compliance" element={<FordCompliancePortal />} />

                        {/* Mercedes-Benz R&D North America — Cross-Document
                      Traceability Walkthrough. Guided single-part narrative
                      (not a dashboard): intake -> extraction -> reconciliation
                      -> supplier follow-up -> resolution -> drift signal.
                      Illustrative/fictional data only. Ungated. */}
                        <Route path="/mercedes-compliance" element={<MercedesCompliancePortal />} />

                        {/* BROSE Tariff Committee Portal — OEM-facing demo tenant
                      (Wednesday). Forked from Denso. Frame: SAT/CFDI/EFOS/32-D
                      verification, per-OEM Section 232 / China-stack exposure,
                      USMCA melt-and-pour. Anchor: Ternium Monterrey mill cert
                      flips BMW seat-structure 232 $1.84M → $0. EN/ES/DE.
                      Ungated — anchor demo must render for the meeting. */}
                        <Route path="/brose-compliance" element={<BroseCompliancePortal />} />

                        {/* GDLS Compliance Portal — Defense SCR demo tenant.
                      Frame: ITAR / CMMC / Berry Amendment / Section 889 / DFARS counterfeit.
                      Tabs: overview, inbox, extraction, berry, subtier, contracts, alerts,
                      attestation. */}
                        <Route path="/gdls-compliance" element={<GDLSCompliancePortal />} />

                        {/* Metalsa Command Center — full IMMEX lifecycle demo
                      tenant (Monday 2026-08-31). Structural clone of BMW,
                      extended with Act 2 (transformation → RVC recompute →
                      Annex 24/31 → MVE → border packet) — see
                      docs/PRD_METALSA_COMMAND_CENTER.md. Ungated — anchor
                      demo must render for the meeting. */}
                        <Route path="/metalsa-compliance" element={<MetalsaCompliancePortal />} />

                        {/* NAFIN Factoring */}
                        <Route
                          path="/factoring"
                          element={
                            <Layout>
                              <FactoringPage />
                            </Layout>
                          }
                        />

                        {/* Nexteer Buyer Portal */}
                        <Route
                          path="/nexteer"
                          element={
                            <Layout>
                              <NexteerPortal />
                            </Layout>
                          }
                        />

                        {/* Bank Portal Routes - No Layout (separate experience for bank partners) */}
                        <Route path="/bank" element={<Navigate to="/bank/dashboard" replace />} />
                        <Route path="/bank/dashboard" element={<BankDashboard />} />
                        <Route path="/bank/company/:id" element={<BankCompanyDetail />} />
                        <Route path="/bank/invoice/:id" element={<BankInvoiceDetail />} />
                        <Route path="/capital-mx" element={<MexicanCapitalPartner />} />
                        <Route
                          path="/capital-mx/expediente/:id"
                          element={<MexicanJacketDetail />}
                        />
                        {/* Data-governance surface for the bank's data lead — contract,
                      dictionary, feed health, retention. Separate route rather than a
                      tab inside the 1,180-line MexicanCapitalPartner. */}
                        <Route path="/capital-mx/gobierno-datos" element={<DataGovernance />} />

                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                    <Toaster />
                  </QueryClientProvider>
                </SimulationStateProvider>
              </GlobalTranslationProvider>
            </PrioritySettingsProvider>
          </SettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
